import React, { Activity, useMemo, useState } from "react";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Check,
  Copy,
  Edit,
  Hash,
  Link2,
  Loader2,
  MapPin,
  Plus,
  RectangleEllipsis,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";

interface PageTopProps {
  backLabel: string;
  title: string;
  count: string;
  subtitle: string;
  actionLabel: string;
  onBack: () => void;
  onAction: () => void;
}

export const PageTop = ({
  backLabel,
  title,
  count,
  subtitle,
  actionLabel,
  onBack,
  onAction,
}: PageTopProps) => (
  <div className="mb-7 flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
    <div className="min-w-0">
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-2 text-blue-700"
      >
        <ArrowLeft className="h-4 w-4" />
        {backLabel}
      </button>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="min-w-0 text-2xl font-bold">{title}</h1>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">
          {count}
        </span>
      </div>
      <p className="mt-2 text-slate-600">{subtitle}</p>
    </div>
    <PrimaryButton
      label={actionLabel}
      onClick={onAction}
      className="w-full sm:w-auto"
    />
  </div>
);

interface CourtHeaderProps {
  icon: React.ReactNode;
  title: string;
  badge: string;
  meta: string[];
  stats: Array<{ label: string; value: number }>;
}

export const CourtHeader = ({
  icon,
  title,
  badge,
  meta,
  stats,
}: CourtHeaderProps) => (
  <section className="flex flex-col gap-5 rounded-xl border border-slate-200 bg-white p-5 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
      <IconBox icon={icon} large />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="min-w-0 text-lg font-bold">{title}</h1>
          <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
            {badge}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-5 text-sm text-slate-500">
          {meta.map((item, index) => (
            <span key={item} className="inline-flex items-center gap-2">
              {index === 1 ? (
                <CalendarDays className="h-4 w-4" />
              ) : (
                <MapPin className="h-4 w-4" />
              )}
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-slate-200 sm:flex sm:divide-x sm:divide-slate-200">
      {stats.map((stat) => (
        <div key={stat.label} className="min-w-24 px-5 py-2 text-center">
          <div className="text-lg font-bold text-blue-700">{stat.value}</div>
          <div className="text-xs text-slate-500">{stat.label}</div>
        </div>
      ))}
    </div>
  </section>
);

export const PrimaryButton = ({
  label,
  onClick,
  className = "",
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-3 rounded-lg bg-blue-600 px-4 py-2 font-normal text-white text-sm hover:bg-blue-700 ${className}`}
  >
    <Plus className="h-5 w-5" />
    {label}
  </button>
);

export const IconBox = ({
  icon,
  large = false,
}: {
  icon: React.ReactNode;
  large?: boolean;
}) => (
  <span
    className={`inline-flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 ${
      large ? "h-14 w-14" : "h-11 w-11"
    }`}
  >
    {icon}
  </span>
);

export const RowActions = ({
  onEdit,
  onDelete,
  loading = false,
  deleteDisabled = false,
}: {
  onEdit: () => void;
  onDelete: () => void;
  loading?: boolean;
  deleteDisabled?: boolean;
}) => (
  <div className="flex gap-3">
    <button
      type="button"
      onClick={onEdit}
      disabled={loading}
      className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
    >
      <Edit className="h-4 w-4" />
    </button>
    <Activity mode={deleteDisabled ? "hidden" : "visible"}>
      <button
        type="button"
        onClick={onDelete}
        disabled={deleteDisabled || loading}
        className="rounded-lg border border-slate-200 p-2 text-red-500 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </Activity>
  </div>
);

export const ResponsiveTableShell = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
    {children}
  </div>
);

export const MobileCardList = ({
  isLoading,
  isEmpty,
  emptyLabel,
  actionLabel,
  onAction,
  children,
}: {
  isLoading: boolean;
  isEmpty: boolean;
  emptyLabel: string;
  actionLabel: string;
  onAction: () => void;
  children: React.ReactNode;
}) => (
  <div className="space-y-3 md:hidden">
    {isLoading ? (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center">
        <Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-600" />
      </div>
    ) : isEmpty ? (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500">
        <div className="mb-4">{emptyLabel}</div>
        <button
          type="button"
          onClick={onAction}
          className="rounded-lg bg-blue-50 px-5 py-3 font-semibold text-blue-700"
        >
          + {actionLabel}
        </button>
      </div>
    ) : (
      children
    )}
  </div>
);

export const MobileInfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start justify-between gap-4 border-t border-slate-100 pt-3 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="min-w-0 text-right font-medium text-slate-700">
      {value}
    </span>
  </div>
);

export const StatusPill = ({ status }: { status?: string }) => {
  const normalized = (status || "active").toLowerCase();
  const active = normalized === "active";
  const label = active ? "Active" : status || "Under Maintenance";

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${
        active ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${active ? "bg-green-600" : "bg-amber-500"}`}
      />
      {label}
    </span>
  );
};
export const MeetingDetails = ({
  meetingId,
  meetingPassword,
  meetingToken,
}: {
  meetingId?: string;
  meetingPassword?: string;
  meetingToken?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const meetingLink = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/join-session?mid=${meetingToken}`;
  }, [meetingId, meetingPassword]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(meetingLink);
      toast.success("Meeting link copied to clipboard");
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 5000);
    } catch (error) {
      console.error("Failed to copy meeting link:", error);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span title="Meeting ID" className="shrink-0 cursor-help text-gray-500">
          <Hash className="h-4 w-4" aria-label="Meeting ID" />
        </span>
        <span className="font-medium">{meetingId}</span>
      </div>

      <div className="flex items-center gap-2">
        <span
          title="Meeting Password"
          className="shrink-0 cursor-help text-gray-500"
        >
          <RectangleEllipsis
            className="h-4 w-4"
            aria-label="Meeting Password"
          />
        </span>
        <span className="font-medium">{meetingPassword}</span>
      </div>

      <div className="flex items-center gap-2">
        <span
          title="Meeting Link"
          className="shrink-0 cursor-help text-gray-500"
        >
          <Link2 className="h-4 w-4" aria-label="Meeting Link" />
        </span>
        <button
          onClick={handleCopy}
          className="rounded-md p-1.5 transition hover:bg-gray-200"
          title="Copy Meeting Link"
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
};

export const LoadingRow = ({ colSpan }: { colSpan: number }) => (
  <tr>
    <td colSpan={colSpan} className="px-6 py-16 text-center">
      <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
    </td>
  </tr>
);

export const EmptyRow = ({
  colSpan,
  label,
  actionLabel,
  onAction,
  hide = false,
  hideLabel = "",
}: {
  colSpan: number;
  label: string;
  actionLabel: string;
  onAction: () => void;
  hide?: boolean;
  hideLabel?: string;
}) =>
  hide ? (
    <tr>
      <td colSpan={4} className="px-6 py-16 text-center text-slate-500">
        <div className="flex flex-col items-center">
          <Building2 className="w-12 h-12 text-gray-300 mb-4" />
          <p className="text-lg font-medium">{hideLabel}</p>
        </div>
      </td>
    </tr>
  ) : (
    <tr>
      <td colSpan={colSpan} className="px-6 py-16 text-center text-slate-500">
        <button
          type="button"
          onClick={onAction}
          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 text-slate-300"
        >
          <Plus className="h-6 w-6" />
        </button>
        <div className="mb-4">{label}</div>
        <button
          type="button"
          onClick={onAction}
          className="rounded-lg bg-blue-50 px-5 py-3 font-semibold text-blue-700"
        >
          + {actionLabel}
        </button>
      </td>
    </tr>
  );
